import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Mail, KeyRound } from "lucide-react";

const GoogleIcon = () => (
  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
);

export const AuthModal = ({ open, onOpenChange, initialMode = "login", initialToken = "" }) => {
  const { login, register, loginGoogle, forgotPassword, resetPassword } = useAuth();
  const [mode, setMode] = useState(initialMode); // login | register | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [token, setTokenVal] = useState(initialToken);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(initialToken ? "reset" : initialMode);
      setTokenVal(initialToken);
      setError("");
      setNotice("");
    }
  }, [open, initialMode, initialToken]);

  const reset = () => {
    setMode("login");
    setEmail("");
    setName("");
    setPassword("");
    setTokenVal("");
    setError("");
    setNotice("");
  };

  const submit = async () => {
    setError("");
    setNotice("");
    setBusy(true);
    let res;
    if (mode === "login") res = await login(email, password);
    else if (mode === "register") res = await register(email, password, name);
    else if (mode === "forgot") res = await forgotPassword(email);
    else res = await resetPassword(token, password);
    setBusy(false);

    if (mode === "forgot") {
      if (res.ok) {
        if (res.reset_link || res.token) {
          setTokenVal(res.token || "");
          setNotice("Demo mode: use the reset link below (normally this is emailed).");
          setMode("reset");
        } else {
          setNotice(res.message || "If that email exists, a reset link has been generated.");
        }
      } else {
        setError(res.error || "Something went wrong");
      }
      return;
    }

    if (res.ok) {
      toast.success(
        mode === "login" ? "Welcome back!" : mode === "register" ? "Account created" : "Password updated"
      );
      onOpenChange(false);
      reset();
    } else {
      setError(res.error || "Something went wrong");
    }
  };

  const titles = {
    login: "Sign in to Burgeon",
    register: "Create your account",
    forgot: "Reset your password",
    reset: "Set a new password",
  };
  const isEmailMode = mode === "login" || mode === "register";
  const canSubmit =
    mode === "forgot" ? !!email : mode === "reset" ? !!password : !!email && !!password;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="auth-modal" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{titles[mode]}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {mode === "forgot"
              ? "Enter your email and we'll generate a reset link."
              : mode === "reset"
              ? "Choose a new password to get back into your account."
              : "Save your game history and unlock facilitator tools. Playing as a guest still works."}
          </DialogDescription>
        </DialogHeader>

        {isEmailMode && (
          <>
            <Button
              type="button"
              onClick={loginGoogle}
              data-testid="google-login-btn"
              variant="outline"
              className="w-full h-11 bg-white text-slate-900 hover:bg-slate-100 border-0 font-semibold"
            >
              <GoogleIcon /> Continue with Google
            </Button>
            <div className="flex items-center gap-3 my-1">
              <div className="h-px bg-slate-800 flex-1" />
              <span className="text-xs text-slate-500">or with email</span>
              <div className="h-px bg-slate-800 flex-1" />
            </div>
          </>
        )}

        <div className="space-y-3">
          {mode === "register" && (
            <div>
              <Label className="text-slate-300 text-xs">Name</Label>
              <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Your name" className="mt-1 bg-slate-950/60 border-slate-700 text-slate-100" />
            </div>
          )}

          {mode !== "reset" && (
            <div>
              <Label className="text-slate-300 text-xs">Email</Label>
              <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="you@example.com" className="mt-1 bg-slate-950/60 border-slate-700 text-slate-100" />
            </div>
          )}

          {mode !== "forgot" && (
            <div>
              <Label className="text-slate-300 text-xs">{mode === "reset" ? "New password" : "Password"}</Label>
              <Input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="••••••••" className="mt-1 bg-slate-950/60 border-slate-700 text-slate-100" />
            </div>
          )}

          {notice && (
            <div data-testid="auth-notice" className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5">
              {notice}
              {mode === "reset" && token && (
                <p className="mt-1 font-mono text-[11px] text-emerald-400 break-all">
                  {window.location.origin}/?reset={token}
                </p>
              )}
            </div>
          )}
          {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}

          <Button
            data-testid="auth-submit-btn"
            onClick={submit}
            disabled={busy || !canSubmit}
            className="w-full h-11 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "forgot" || mode === "reset" ? <KeyRound className="w-4 h-4 mr-1.5" /> : <Mail className="w-4 h-4 mr-1.5" />}
            {mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : mode === "forgot" ? "Generate Reset Link" : "Update Password"}
          </Button>

          {mode === "login" && (
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
              className="w-full text-center text-xs text-slate-400 hover:text-emerald-400"
            >
              Forgot your password?
            </button>
          )}
        </div>

        <p className="text-center text-sm text-slate-400">
          {mode === "login" ? "New to Burgeon?" : mode === "register" ? "Already have an account?" : "Remembered it?"}{" "}
          <button
            type="button"
            data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "register" ? "login" : mode === "login" ? "register" : "login"); setError(""); setNotice(""); }}
            className="text-emerald-400 font-semibold hover:underline"
          >
            {mode === "login" ? "Create an account" : mode === "register" ? "Sign in" : "Back to sign in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};
